import { Heading } from "@elaniintc/goes-design-system";
import { PaymentRow } from "./PaymentRow";
import type { PaymentRequest } from "@/queries/payment";

interface PaymentSummaryProps {
  paymentData: PaymentRequest;
}

export const PaymentSummary = ({ paymentData }: PaymentSummaryProps) => {
  const getActivePayment = () => {
    if (!paymentData.payments || paymentData.payments.length === 0) return null;
    return (
      paymentData.payments.find((p) => p.status === "PAID") ??
      paymentData.payments.find((p) => p.status === "PENDING") ??
      null
    );
  };

  const getReferenceCode = (): string => {
    const payment = getActivePayment();
    return (payment?.metadata?.internal_payment_reference as string) ?? "-";
  };
  const formatCurrency = (amount: string | number) => {
    let numAmount: number;

    if (typeof amount === "string") {
      numAmount = parseFloat(amount.replace(/,/g, ""));
    } else {
      numAmount = amount;
    }

    if (isNaN(numAmount)) {
      return "$0.00";
    }

    return new Intl.NumberFormat("es-US", {
      style: "currency",
      currency: "USD",
    }).format(numAmount);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    const formattedDate = date.toLocaleDateString("es-ES", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
    const formattedTime = date.toLocaleTimeString("es-ES", {
      hour: "2-digit",
      minute: "2-digit",
    });
    return `${formattedDate} | ${formattedTime}`;
  };

  const getTotalColorClass = (): string => {
    switch (paymentData.status) {
      case "PAID":
        return "text-success-quiet";
      case "EXEMPT":
        return "text-body-quiet";
      case "REJECTED":
        return "text-danger-quiet";
      case "PENDING":
      default:
        return "text-warning-quiet";
    }
  };

  const getPaymentMethodLabel = (method: string | null): string => {
    if (!method) return "Pendiente";
    switch (method) {
      case "CREDIT_CARD":
        return "Tarjeta de crédito";
      case "NPE":
        return "NPE";
      default:
        return method;
    }
  };

  const getApplicantName = (): string => {
    if (!paymentData.applicant) return "Pendiente";
    const { first_name, last_name } = paymentData.applicant;
    return `${first_name} ${last_name}`.trim() || "Pendiente";
  };

  return (
    <div className="bg-neutral-object border border-neutral-quiet rounded-xl">
      <Heading variant="h6" className="border-b border-neutral-quiet p-3">
        Resumen de pago
      </Heading>
      <div className="px-3">
        <PaymentRow
          label="Referencia"
          value={getReferenceCode()}
          valueColor="loud"
          useMonoFont
        />
        <PaymentRow
          label="Fecha de pago"
          value={
            paymentData.status === "PAID" && paymentData.updated_at
              ? formatDate(paymentData.updated_at)
              : paymentData.status === "EXEMPT"
                ? "Eximido"
                : "Pendiente"
          }
          valueColor={
            paymentData.status === "EXEMPT"
              ? "quiet"
              : paymentData.status === "PAID"
                ? "loud"
                : "quiet"
          }
        />
        <PaymentRow
          label="Método de pago"
          value={
            paymentData.status === "PAID" &&
            getActivePayment()?.metadata?.payment_method_details
              ? getPaymentMethodLabel(
                  getActivePayment()?.metadata
                    ?.payment_method_details as string,
                )
              : paymentData.status === "EXEMPT"
                ? "Eximido"
                : "Pendiente"
          }
          valueColor={
            paymentData.status === "EXEMPT"
              ? "quiet"
              : paymentData.status === "PAID"
                ? "loud"
                : "quiet"
          }
        />
        <PaymentRow
          label="Pagador"
          value={
            paymentData.status === "EXEMPT" ? "Eximido" : getApplicantName()
          }
          valueColor={
            paymentData.status === "EXEMPT"
              ? "quiet"
              : paymentData.status === "PAID"
                ? "loud"
                : !paymentData.applicant
                  ? "quiet"
                  : "loud"
          }
        />
        <PaymentRow
          label={
            paymentData.status === "PENDING"
              ? "Total a pagar"
              : paymentData.status === "EXEMPT"
                ? "Total eximido"
                : "Total pagado"
          }
          value={formatCurrency(paymentData.amount)}
          valueColor={getTotalColorClass()}
          isTotal
        />
      </div>
    </div>
  );
};
